import React, { useEffect, useState } from 'react'
import { Col, Container, Form, Row, Stack } from 'react-bootstrap'
import {
  DEFAULT_CONTACT_SHARING_ENABLED,
  DEFAULT_DOCUMENT_SHARING_ENABLED,
  DEFAULT_SCHEDULING_SHARING_ENABLED,
  DEFAULT_SESSION_SHARING_ENABLED,
  useGetAccountQuery,
  useGetAccountUserSettingsQuery,
  useUpdateAccountMutation,
  useUpdateAccountUserSettingsMutation,
} from '../../../store/accountUserSettings/accountUserSettings'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { MultiScopedBlock, ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../../components/Button/Button'
import PrizeBadge from '../../../components/ScopedBlock/PrizeBadge.svg?url'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { addToast } from '../../../store/toast/toastSlice'
import { SelectDropdown, SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { FormFeedback } from '../../../components/Form/FormFeedback'
import { useGetAllUsersQuery } from '../../../store/user/user'
import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { UserOwner, UserOwnerType } from '../../../models/User'
import classNames from 'classnames'
import './CollaborationSettings.scss'
import { UpdatePrivateMeetingsSettingModal } from './UpdatePrivateMeetingsSettingModal'
import { usePrivateMeetingsEnabled } from '../../../common/usePrivateMeetingsEnabled'

interface FormValues {
  sessionSharingEnabled: boolean
  contactSharingEnabled: boolean
  documentSharingEnabled: boolean
  schedulingSharingEnabled: boolean
  defaultContactOwnership?: string | null
  privateMeetings: boolean
  overrideUserSettings: boolean
}

export const CollaborationSettings: React.FC = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)

  const defaultValues = {
    sessionSharingEnabled: true,
    contactSharingEnabled: false,
    documentSharingEnabled: false,
    schedulingSharingEnabled: false,
    defaultContactOwnership: null,
    privateMeetings: true,
    overrideUserSettings: false,
  }
  const { setValue, control, handleSubmit } = useForm<FormValues>({ defaultValues })

  const contactSharingEnabledValue = useWatch({ control, name: 'contactSharingEnabled' })

  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery({ accountId })
  const { privateMeetingsEnabled, privateMeetingsSetting, privateMeetingsQuota } = usePrivateMeetingsEnabled()
  const privateMeetingsEnterpriseControlled = account?.data.enterpriseConfig.privateMeetings != null

  const navigate = useNavigate()
  const { hasQuota } = useZocksQuota()
  const sessionSharingAvailable = hasQuota('identity.sessionSharingEnabled.value')
  const documentSharingAvailable = hasQuota('ai.documentSummaryAgent.value')
  const schedulingSharingAvailable = hasQuota('scheduling.enabled.value')

  const { data: accountUserSettings, isFetching: accountUserSettingsLoading } = useGetAccountUserSettingsQuery(
    { accountId },
    { refetchOnMountOrArgChange: true, skip: !sessionSharingAvailable },
  )
  const { data: users, isFetching: usersFetching } = useGetAllUsersQuery({
    accountId: accountId,
    identityTypes: ['idp'],
  })
  const isSharingEnterpriseManaged = accountUserSettings?.data.enterpriseManaged ?? false

  const userOptions: SelectOption[] = [
    { id: 'null', label: 'No one', className: 'no-one-option' },
    { id: 'current-user', label: 'Creating user', className: 'current-user-option' },
    ...(users?.data.map((user) => {
      return { id: user.id, label: getUserDisplayName(user) }
    }) ?? []),
  ]
  const [updateAccountUserSettings, { isLoading: userSettingsSaveLoading, isSuccess: isUserSettingsSaveSuccess }] = useUpdateAccountUserSettingsMutation()
  const [updateAccount, { isLoading: isAccountUpdating }] = useUpdateAccountMutation()

  const [showUpdatePrivateMeetingsSettingModal, setShowUpdatePrivateMeetingsSettingModal] = useState<boolean>(false)

  useEffect(() => {
    if (!accountUserSettings) {
      return
    }
    const accountSettingsOverride = accountUserSettings.data.settingsOverride
    setValue('sessionSharingEnabled', accountSettingsOverride.sessionSharingEnabled ?? DEFAULT_SESSION_SHARING_ENABLED)
    setValue('contactSharingEnabled', accountSettingsOverride.contactSharingEnabled ?? DEFAULT_CONTACT_SHARING_ENABLED)
    setValue('documentSharingEnabled', accountSettingsOverride.documentSharingEnabled ?? DEFAULT_DOCUMENT_SHARING_ENABLED)
    setValue('schedulingSharingEnabled', accountSettingsOverride.schedulingSharingEnabled ?? DEFAULT_SCHEDULING_SHARING_ENABLED)
    setValue('defaultContactOwnership', accountSettingsOverride.defaultContactOwnership?.ownerId ?? null)
    setValue('overrideUserSettings', accountUserSettings.data.overrideUserSettings ?? false)
  }, [accountUserSettings, setValue])

  useEffect(() => {
    if (!account) {
      return
    }
    setValue('privateMeetings', privateMeetingsEnabled)
  }, [account, privateMeetingsEnabled, setValue])

  const onSubmit = async (data: FormValues) => {
    await updateAccountUserSettings({
      accountId,
      payload: {
        overrideUserSettings: data.overrideUserSettings,
        settingsOverride: {
          ...accountUserSettings?.data.settingsOverride,
          sessionSharingEnabled: data.sessionSharingEnabled,
          contactSharingEnabled: data.contactSharingEnabled,
          documentSharingEnabled: data.contactSharingEnabled ? data.documentSharingEnabled : false,
          schedulingSharingEnabled: data.contactSharingEnabled ? data.schedulingSharingEnabled : false,
          defaultContactOwnership: getDefaultContactOwnerShip(data.defaultContactOwnership),
        },
      },
    })
  }

  const applyChangePrivateMeetingsSetting = async (changePastMeetings: boolean) => {
    if (!account) {
      return
    }
    const accountConfig = { ...account.data.config, privateMeetings: !privateMeetingsSetting }
    updateAccount({
      accountId: accountId,
      payload: {
        config: accountConfig,
        options: { changeExistingPrivateMeetingsToPublic: changePastMeetings },
      },
    })
    setShowUpdatePrivateMeetingsSettingModal(false)
  }

  const handleChangePrivateMeetingsSetting = async () => {
    if (privateMeetingsSetting) {
      setShowUpdatePrivateMeetingsSettingModal(true)
      return
    }
    await applyChangePrivateMeetingsSetting(false)
  }

  const getDefaultContactOwnerShip = (ownerId?: string | null): UserOwner | null => {
    if (!ownerId || ownerId === 'null') {
      return null
    }
    return { ownerType: ownerId === 'current-user' ? UserOwnerType.CURRENT_USER : UserOwnerType.USER, ownerId }
  }

  const sharingSwitchesDisabled = !sessionSharingAvailable || accountUserSettingsLoading || userSettingsSaveLoading || isSharingEnterpriseManaged
  const contactDependentSwitchesDisabled = sharingSwitchesDisabled || !contactSharingEnabledValue

  useEffect(() => {
    if (isUserSettingsSaveSuccess) {
      dispatch(
        addToast({
          variant: 'success',
          title: 'Sharing settings saved',
          messages: ['Sharing settings saved successfully.'],
        }),
      )
    }
  }, [dispatch, isUserSettingsSaveSuccess])

  return (
    <>
      {showUpdatePrivateMeetingsSettingModal ? (
        <UpdatePrivateMeetingsSettingModal
          show={showUpdatePrivateMeetingsSettingModal}
          onConfirm={applyChangePrivateMeetingsSetting}
          onCancel={() => setShowUpdatePrivateMeetingsSettingModal(false)}
        />
      ) : null}
      <Container fluid className='p-0'>
        <Row className='zocks-mb-2'>
          <Col className='p-0'>
            <h4 className='zocks-text-gray-900'>Private meetings</h4>
          </Col>
        </Row>
        <Row className='zocks-mb-1.25 p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
          <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
            <Stack direction='horizontal' gap={3}>
              <div className='d-flex align-self-center gap-3 align-items-center'>
                <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                <span className='zocks-text-gray-900'>Allow users to create and sync private meetings that are only visible to participants.</span>
              </div>
              {!privateMeetingsQuota ? (
                <ScopedBlock
                  scope={{
                    scope: 'account_config:create',
                    superScope: ['account_config:admin'],
                  }}
                  missingScopeAction={MissingScopeAction.DISABLE}
                >
                  <Button
                    className='d-flex p-1 p-2 align-items-center text-nowrap'
                    variant='primary'
                    onClick={() => {
                      navigate('/account/plan')
                    }}
                  >
                    <img alt='Upgrade' src={PrizeBadge} />
                    Get feature
                  </Button>
                </ScopedBlock>
              ) : null}
            </Stack>
          </Col>
        </Row>
        <Row>
          <Container
            className={classNames({
              'm-0 p-3 zocks-border-0.25 zocks-bg-color-gray-100 d-flex flex-column gap-3': privateMeetingsEnterpriseControlled,
              'm-0 px-0 py-3': !privateMeetingsEnterpriseControlled,
            })}
          >
            {privateMeetingsEnterpriseControlled ? (
              <Row className='m-0'>
                <Col className='p-0 d-flex align-items-center zocks-gap-2.5 align-self-center'>
                  <span className='paragraph-medium medium zocks-text-gray-900'>Disabled by Enterprise Administrator, can't be changed on account level.</span>
                </Col>
              </Row>
            ) : null}
            <Row className='mx-0'>
              <Col className='p-0'>
                <div className='paragraph-medium'>Enable private meetings</div>
              </Col>
              <Col className='p-0'>
                <Controller
                  name='privateMeetings'
                  control={control}
                  render={({ field }) => {
                    return (
                      <MultiScopedBlock
                        scopedBlocks={[
                          {
                            missingScopeAction: MissingScopeAction.DISABLE,
                            scope: {
                              scope: 'account_config:create',
                              superScope: ['account_config:admin'],
                            },
                          },
                          {
                            missingScopeAction: MissingScopeAction.DISABLE,
                            scope: { scope: 'account:compliance_config_update', superScope: [] },
                          },
                        ]}
                      >
                        <Form.Switch
                          disabled={isAccountLoading || isAccountUpdating || privateMeetingsEnterpriseControlled || !privateMeetingsQuota}
                          checked={field.value}
                          onChange={() => {
                            handleSubmit(handleChangePrivateMeetingsSetting)()
                          }}
                        />
                      </MultiScopedBlock>
                    )
                  }}
                />
              </Col>
            </Row>
          </Container>
        </Row>
        <Row className='zocks-mb-2 mt-4'>
          <Col className='p-0'>
            <h4 className='zocks-text-gray-900'>Set default sharing settings for your team</h4>
          </Col>
        </Row>
        <Row className='zocks-mb-2 p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
          <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
            <Stack direction='horizontal' gap={3}>
              <div className='d-flex align-self-center gap-3 align-items-center'>
                <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                <span className='zocks-text-gray-900'>
                  {documentSharingAvailable
                    ? 'These options allows to customize the default value for sharing meetings, contacts and documents among team members. While it sets to default, individual users can override this setting as needed, enabling or disabling meeting, contact and document sharing based on their personal preferences.'
                    : 'These options allows to customize the default value for sharing meetings and contacts among team members. While it sets to default, individual users can override this setting as needed, enabling or disabling meeting and contact sharing based on their personal preferences.'}
                </span>
              </div>
              {!sessionSharingAvailable && (
                <ScopedBlock
                  scope={{
                    scope: 'account_config:create',
                    superScope: ['account_config:admin'],
                  }}
                  missingScopeAction={MissingScopeAction.DISABLE}
                >
                  <Button
                    className='d-flex p-1 p-2 align-items-center text-nowrap'
                    variant='primary'
                    onClick={() => {
                      navigate('/account/plan')
                    }}
                  >
                    <img alt='Upgrade' src={PrizeBadge} />
                    Get feature
                  </Button>
                </ScopedBlock>
              )}
            </Stack>
          </Col>
        </Row>
        {isSharingEnterpriseManaged && (
          <Row className='zocks-mb-2 p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
            <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
              <Stack direction='horizontal' gap={3}>
                <div className='d-flex align-self-center gap-3 align-items-center'>
                  <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                  <span className='zocks-text-gray-900'>Default sharing settings are managed at the enterprise level and cannot be modified here.</span>
                </div>
              </Stack>
            </Col>
          </Row>
        )}
        <Row className='zocks-mb-2 gap-3 p-0 align-items-center'>
          <Col className='p-0'>
            <div className='paragraph-medium'>
              Share <b>meetings</b> between your teammates
            </div>
          </Col>
          <Col className='p-0'>
            <Controller
              name='sessionSharingEnabled'
              control={control}
              render={({ field }) => {
                return (
                  <ScopedBlock
                    scope={{
                      scope: 'account_config:create',
                      superScope: ['account_config:admin'],
                    }}
                    missingScopeAction={MissingScopeAction.DISABLE}
                  >
                    <Form.Switch
                      disabled={sharingSwitchesDisabled}
                      checked={field.value && sessionSharingAvailable}
                      onChange={(e) => {
                        field.onChange(e)
                        handleSubmit(onSubmit)()
                      }}
                    />
                  </ScopedBlock>
                )
              }}
            />
          </Col>
        </Row>
        <Row className='zocks-mb-2 gap-3 p-0 align-items-center'>
          <Col className='p-0'>
            <div className='paragraph-medium'>
              Share <b>contacts</b> between your teammates
            </div>
          </Col>
          <Col className='p-0'>
            <Controller
              name='contactSharingEnabled'
              control={control}
              render={({ field }) => {
                return (
                  <ScopedBlock
                    scope={{
                      scope: 'account_config:create',
                      superScope: ['account_config:admin'],
                    }}
                    missingScopeAction={MissingScopeAction.DISABLE}
                  >
                    <Form.Switch
                      disabled={sharingSwitchesDisabled}
                      checked={field.value && sessionSharingAvailable}
                      onChange={(e) => {
                        field.onChange(e)
                        handleSubmit(onSubmit)()
                      }}
                    />
                  </ScopedBlock>
                )
              }}
            />
          </Col>
        </Row>

        {documentSharingAvailable ? (
          <Row className='zocks-mb-2 gap-3 p-0 align-items-center'>
            <Col className='p-0'>
              <div className='paragraph-medium'>
                Share <b>documents</b> between your teammates
              </div>
            </Col>
            <Col className='p-0'>
              <Controller
                name='documentSharingEnabled'
                control={control}
                render={({ field }) => {
                  return (
                    <ScopedBlock
                      scope={{
                        scope: 'account_config:create',
                        superScope: ['account_config:admin'],
                      }}
                      missingScopeAction={MissingScopeAction.DISABLE}
                    >
                      <Form.Switch
                        disabled={contactDependentSwitchesDisabled}
                        checked={field.value && contactSharingEnabledValue}
                        onChange={(e) => {
                          field.onChange(e)
                          handleSubmit(onSubmit)()
                        }}
                      />
                    </ScopedBlock>
                  )
                }}
              />
            </Col>
          </Row>
        ) : null}

        {schedulingSharingAvailable ? (
          <Row className='zocks-mb-2 gap-3 p-0 align-items-center'>
            <Col className='p-0'>
              <div className='paragraph-medium'>
                Share <b>bookings</b> between your teammates
              </div>
            </Col>
            <Col className='p-0'>
              <Controller
                name='schedulingSharingEnabled'
                control={control}
                render={({ field }) => {
                  return (
                    <ScopedBlock
                      scope={{
                        scope: 'account_config:create',
                        superScope: ['account_config:admin'],
                      }}
                      missingScopeAction={MissingScopeAction.DISABLE}
                    >
                      <Form.Switch
                        disabled={contactDependentSwitchesDisabled}
                        checked={field.value && contactSharingEnabledValue}
                        onChange={(e) => {
                          field.onChange(e)
                          handleSubmit(onSubmit)()
                        }}
                      />
                    </ScopedBlock>
                  )
                }}
              />
            </Col>
          </Row>
        ) : null}

        <Row className='zocks-mb-2 gap-3 p-0 align-items-center'>
          <Col className='p-0'>
            <div className='paragraph-medium'>
              Overwrite <b>individuals</b> collaboration settings
            </div>
          </Col>
          <Col className='p-0'>
            <Controller
              name='overrideUserSettings'
              control={control}
              render={({ field }) => {
                return (
                  <ScopedBlock
                    scope={{
                      scope: 'account_config:create',
                      superScope: ['account_config:admin'],
                    }}
                    missingScopeAction={MissingScopeAction.DISABLE}
                  >
                    <Form.Switch
                      disabled={sharingSwitchesDisabled}
                      checked={field.value && sessionSharingAvailable}
                      onChange={(e) => {
                        field.onChange(e)
                        handleSubmit(onSubmit)()
                      }}
                    />
                  </ScopedBlock>
                )
              }}
            />
          </Col>
        </Row>

        <Row className='mb-3'>
          <Col className='p-0'>
            <h5 className='zocks-text-gray-900'>Set default contact ownership</h5>
          </Col>
        </Row>
        <Row>
          <Col xs={6} className='p-0'>
            <Controller
              name='defaultContactOwnership'
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <ScopedBlock
                    scope={{
                      scope: 'account_config:create',
                      superScope: ['account_config:admin'],
                    }}
                    missingScopeAction={MissingScopeAction.DISABLE}
                  >
                    <SelectDropdown
                      className='zocks-px-0.75 py-2'
                      options={userOptions}
                      initialSelectedOption={userOptions.find((option) => option.id === field.value)}
                      onOptionSelected={(option) => {
                        field.onChange(option.id)
                        handleSubmit(onSubmit)()
                      }}
                      disabled={usersFetching || accountUserSettingsLoading || userSettingsSaveLoading}
                    />
                  </ScopedBlock>
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </>
              )}
            />
          </Col>
        </Row>
      </Container>
    </>
  )
}
